import { defineStore } from 'pinia'
import { ref } from 'vue'
import { backgroundTaskRepository } from '@/infrastructure'
import { listBackgroundTasks, loadBackgroundTaskMessages } from '@/application/backgroundTask/backgroundTasks'
import { ACTIVE_TASK_STATES, type BackgroundTask, type BackgroundTaskKind } from '@/domain/backgroundTask/BackgroundTask'
import { useUserStore } from '@/store/user'

export const TASKS_ACTIVE_REFRESH_MS = 5_000
export const TASKS_IDLE_REFRESH_MS = 60_000

/** The kinds of the tasks that just finished, so a view can reload what they wrote. */
export interface FinishedTasks {
  seq: number
  kinds: BackgroundTaskKind[]
}

export const useBackgroundTasksStore = defineStore('backgroundTasks', () => {
  const tasks = ref<BackgroundTask[]>([])
  const activeCount = ref(0)
  const loaded = ref(false)
  const messagesByTask = ref<Record<string, string[]>>({})
  const lastFinished = ref<FinishedTasks>({ seq: 0, kinds: [] })
  // why: a short upload can already be finished on the first refresh after it was queued, never seen running.
  const tracked = new Set<string>()
  let timer: ReturnType<typeof setTimeout> | null = null
  let watchers = 0
  let latestRefresh = 0
  let loadedFor: number | null | undefined

  async function refresh(): Promise<void> {
    if (document.visibilityState !== 'visible') return
    const userId = useUserStore().id ?? null
    if (userId !== loadedFor) clear()
    loadedFor = userId
    const current = ++latestRefresh
    try {
      const data = await listBackgroundTasks(backgroundTaskRepository)
      if (current !== latestRefresh) return
      announceFinished(data.tasks)
      tasks.value = data.tasks
      activeCount.value = data.activeCount
      loaded.value = true
    } catch {
      return
    } finally {
      if (current === latestRefresh) schedule()
    }
  }

  // why: a failed task can also have written part of its rows, so it is announced like a finished one.
  function announceFinished(next: BackgroundTask[]): void {
    const wasActive = new Set([...tracked, ...tasks.value.filter((task) => ACTIVE_TASK_STATES.includes(task.state)).map((task) => task.taskId)])
    const finished = next.filter((task) => wasActive.has(task.taskId) && !ACTIVE_TASK_STATES.includes(task.state))
    finished.forEach((task) => tracked.delete(task.taskId))
    if (finished.length > 0) lastFinished.value = { seq: lastFinished.value.seq + 1, kinds: finished.map((task) => task.kind) }
  }

  function track(taskId: string): void {
    tracked.add(taskId)
  }

  // why: polls fast only while something runs, so an idle panel costs one request a minute.
  function schedule(): void {
    if (timer) clearTimeout(timer)
    if (watchers === 0) return
    timer = setTimeout(() => void refresh(), activeCount.value > 0 ? TASKS_ACTIVE_REFRESH_MS : TASKS_IDLE_REFRESH_MS)
  }

  function onVisibility(): void {
    if (document.visibilityState === 'visible') void refresh()
  }

  function start(): void {
    watchers += 1
    if (watchers > 1) return
    void refresh()
    document.addEventListener('visibilitychange', onVisibility)
  }

  function stop(): void {
    watchers = Math.max(0, watchers - 1)
    if (watchers > 0) return
    if (timer) clearTimeout(timer)
    timer = null
    document.removeEventListener('visibilitychange', onVisibility)
  }

  async function loadMessages(taskId: string): Promise<void> {
    if (messagesByTask.value[taskId]) return
    const messages = await loadBackgroundTaskMessages(backgroundTaskRepository, taskId)
    messagesByTask.value = { ...messagesByTask.value, [taskId]: messages }
  }

  function clear(): void {
    latestRefresh++
    tasks.value = []
    activeCount.value = 0
    loaded.value = false
    messagesByTask.value = {}
    tracked.clear()
    loadedFor = undefined
  }

  return { tasks, activeCount, loaded, messagesByTask, lastFinished, refresh, start, stop, track, loadMessages, clear }
})
